import React, { useEffect, useState, useCallback } from "react";
import { Search, CheckCircle, TriangleAlert } from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import { DownArrow, PlusIcon, UpArrow } from "../svg/Icons";
import RowsPerPageSelector from "../common/RowsPerPageSelector";
import Pagination from "../common/Pagination";
import LogButton from "../common/LogButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { styles } from "../../styles/pages/Product/ProductStyle";
import Loader from "../common/Loader";
import { ReactComponent as DownloadIcon } from "../../icons/ExcelDownloadIcon.svg";
import { Tooltip } from "../../components/common/Tooltip";

type StatusType = "Active" | "Inactive";

interface Product {
  id: string;
  prod_name: string;
  prod_cost: string;
  prod_price: string;
  prod_type: string;
  prod_url: string;
  prod_part_name: string;
  effective_date: string | null;
  prod_part_number: string;
  status: StatusType;
  prod_cat_name: string;
  subcat_name: string;
  prod_img1: string | null;
  prod_img2: string | null;
  prod_img3: string | null;
  log: string;
}

const ProductTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [products, setProducts] = useState<Product[]>([]);
  const [sortColumn, setSortColumn] = useState<keyof Product | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { isOpen, openModal, closeModal } = useModal();
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [isErrorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Helper function to display "--" for empty fields
  const displayValue = (value: string | null | undefined): string => {
    return value === null || value === undefined || value === "" ? "--" : value;
  };

  const getAuthToken = (): string | null => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      console.error("No auth token found.");
    }
    return token;
  };

  const handleDeleteClick = (productId: string) => {
    setSelectedProductId(productId);
    openModal();
  };

  const handleConfirmDelete = async () => {
    if (!selectedProductId) return;

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/delete`,
        { id: selectedProductId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 200) {
        setProducts((prevProducts) => {
          const updatedProducts = prevProducts.filter(
            (p) => p.id !== selectedProductId
          );
          const filteredUpdatedProducts = updatedProducts.filter((product) =>
            Object.values(product)
              .join(" ")
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          );

          const sortedUpdatedProducts = [...filteredUpdatedProducts].sort((a, b) => {
            if (!sortColumn) return 0;
            const valA = a[sortColumn];
            const valB = b[sortColumn];
            if (typeof valA === "string" && typeof valB === "string") {
              return sortOrder === "asc"
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
            }
            return sortOrder === "asc"
              ? (Number(valA) || 0) - (Number(valB) || 0)
              : (Number(valB) || 0) - (Number(valA) || 0);
          });

          const newDisplayedProducts = sortedUpdatedProducts.slice(
            (currentPage - 1) * rowsPerPage,
            currentPage * rowsPerPage
          );

          if (newDisplayedProducts.length === 0 && currentPage > 1) {
            setCurrentPage((prev) => Math.max(prev - 1, 1));
          }

          return updatedProducts;
        });
        setSuccessMessage("Product Deleted Successfully!");
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
        closeModal();
      } else {
        throw new Error(response.data.message || "Failed to delete product.");
      }
    } catch (err) {
      console.error("Network error:", err);
      const errorMsg = err instanceof Error ? err.message : "Failed to delete product.";
      closeModal();
      if (errorMsg === "Can't delete this product because it's assigned to subcategory.") {
        window.location.href = "/product-list";
      } else {
        setErrorMessage(errorMsg);
        setErrorModalOpen(true);
      }
    }
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/list`,
        { search: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 200) {
        const formattedProducts: Product[] = response.data.data.map(
          (product: any) => ({
            id: product.id,
            prod_name: product.prod_name,
            prod_cost: product.prod_cost,
            prod_price: product.prod_price,
            prod_type: product.prod_type,
            prod_url: product.prod_url,
            prod_part_name: product.prod_part_name,
            effective_date: product.effective_date,
            prod_part_number: product.prod_part_number,
            status: product.status === 1 ? "Active" : "Inactive",
            prod_cat_name: product.prod_cat_name,
            subcat_name: product.subcat_name,
            prod_img1: product.prod_img1,
            prod_img2: product.prod_img2,
            prod_img3: product.prod_img3,
            log: product.log,
          })
        );
        setProducts(formattedProducts);
      } else {
        setProducts([]);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred"
      );
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSort = (column: keyof Product) => {
    setSortColumn(column);
    setSortOrder(sortColumn === column && sortOrder === "asc" ? "desc" : "asc");
  };

  const filteredProducts = products.filter((product) =>
    Object.values(product)
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (!sortColumn) return 0;
    const valA = a[sortColumn];
    const valB = b[sortColumn];
    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }
    return sortOrder === "asc"
      ? (Number(valA) || 0) - (Number(valB) || 0)
      : (Number(valB) || 0) - (Number(valA) || 0);
  });

  const displayedProducts = sortedProducts.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredProducts.length / rowsPerPage);

  const handleDownload = () => {
    const dataToDownload = searchTerm
      ? filteredProducts
      : sortColumn
        ? sortedProducts
        : products;

    if (!dataToDownload.length) {
      setErrorMessage("No data available to download.");
      setErrorModalOpen(true);
      return;
    }

    const formattedData = dataToDownload.map((product) => ({
      "Product Name": displayValue(product.prod_name),
      Type: displayValue(product.prod_type),
      "Part Name": displayValue(product.prod_part_name),
      "Effective Date": displayValue(product.effective_date),
      "Part Number": displayValue(product.prod_part_number),
      Category: displayValue(product.prod_cat_name),
      Subcategory: displayValue(product.subcat_name),
      Status: product.status,
      Log: displayValue(product.log),
    }));

    const headers = Object.keys(formattedData[0]);
    const worksheet = XLSX.utils.aoa_to_sheet([]);

    // 1. Add title in A1
    XLSX.utils.sheet_add_aoa(worksheet, [["Product Data List"]], {
      origin: "A1",
    });

    // 2. Merge title across all columns
    worksheet["!merges"] = [
      {
        s: { r: 0, c: 0 },
        e: { r: 0, c: headers.length - 1 },
      },
    ];

    // 3. Style the title cell
    worksheet["A1"].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // 4. Add headers in row 2
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A2" });

    // 5. Bold header cells
    headers.forEach((_, i) => {
      const col = String.fromCharCode(65 + i); // A, B, C...
      const cell = `${col}2`;
      if (worksheet[cell]) {
        worksheet[cell].s = {
          font: { bold: true },
        };
      }
    });

    // 6. Add data starting from row 3
    XLSX.utils.sheet_add_json(worksheet, formattedData, {
      origin: "A3",
      skipHeader: true,
    });

    // 7. Auto-size columns
    worksheet["!cols"] = headers.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...formattedData.map(
            (row) =>
              (row[key as keyof typeof row] as string)?.toString().length || 0
          ),
          "Product Data List".length
        ) + 2,
    }));

    // 8. Export to file
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Products");
    XLSX.writeFile(
      workbook,
      searchTerm ? `ProductData_${searchTerm}.xlsx` : "ProductData.xlsx"
    );
  };

  const toggleStatus = async (id: string) => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const product = products.find((s) => s.id === id);
      if (!product) return;
      const newStatus = product.status === "Active" ? 0 : 1;
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/status/update`,
        { id: id, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 200) {
        setProducts((prev) =>
          prev.map((s) =>
            s.id === id
              ? {
                ...s,
                status: newStatus === 1 ? "Active" : "Inactive",
              }
              : s
          )
        );
        setSuccessMessage("Product Status Updated Successfully!");
        setSuccessModalOpen(true);
        setTimeout(() => setSuccessModalOpen(false), 2000);
      } else {
        setErrorMessage(response.data.message || "Failed to update product status.");
        setErrorModalOpen(true);
      }
    } catch (err) {
      console.error("Network error:", err);
      setErrorMessage("An unexpected error occurred while updating the product status.");
      setErrorModalOpen(true);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>Product List</h3>
      </div>
      <div className={styles.tableCard}>
        <div className={styles.headerControls}>
          <RowsPerPageSelector
            rowsPerPage={rowsPerPage}
            setRowsPerPage={(val) => {
              setRowsPerPage(val);
              setCurrentPage(1);
            }}
          />
          <div className={styles.controlsInner}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search..."
                className={styles.searchInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button className={styles.downloadButton} onClick={handleDownload}>
              Download
              <DownloadIcon className={styles.downloadIcon} />
            </button>
            <Link to="/product-add" className={styles.addButtonContainer}>
              <button className={styles.addButton}>
                Add New Product
                <PlusIcon />
              </button>
            </Link>
          </div>
        </div>

        {error && <p className={styles.errorText}>{error}</p>}
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr className={styles.theadRow}>
                <th
                  className={styles.thName}
                  onClick={() => handleSort("prod_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Product Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("prod_type")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Type</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("prod_part_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Part Name</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("effective_date")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Effective Date</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("prod_part_number")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Part Number</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("prod_cat_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Category</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th
                  className={styles.thRole}
                  onClick={() => handleSort("subcat_name")}
                >
                  <div className={styles.thSortable}>
                    <p className={styles.thText}>Subcategory</p>
                    <button className={styles.sortButton}>
                      <DownArrow />
                      <UpArrow />
                    </button>
                  </div>
                </th>
                <th className={styles.thRole}>
                  <p className={styles.thText}>Status</p>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Action</p>
                </th>
                <th className={styles.thAction}>
                  <p className={styles.thText}>Log</p>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr className={styles.loadingRow}>
                  <td colSpan={8} className="text-center py-4">
                    <Loader />
                  </td>
                </tr>
              ) : displayedProducts.length ? (
                displayedProducts.map((product) => (
                  <tr key={product.id} className={styles.tbodyRow}>
                    <td className={styles.tdName}>
                      {displayValue(product.prod_name)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.prod_type)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.prod_part_name)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.effective_date)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.prod_part_number)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.prod_cat_name)}
                    </td>
                    <td className={styles.tdRole}>
                      {displayValue(product.subcat_name)}
                    </td>
                    <td className={styles.tdRole}>
                      <div className={styles.statusContainer}>
                        <button
                          onClick={() => toggleStatus(product.id)}
                          className={`${styles.statusToggle} ${product.status === "Active"
                            ? styles.statusToggleActive
                            : styles.statusToggleInactive
                            }`}
                        >
                          <div
                            className={`${styles.statusToggleCircle} ${product.status === "Active"
                              ? styles.statusToggleCircleActive
                              : styles.statusToggleCircleInactive
                              }`}
                          ></div>
                        </button>
                        <span
                          className={`${styles.statusBadge} ${product.status === "Active"
                            ? styles.statusActive
                            : styles.statusInactive
                            }`}
                        >
                          {product.status}
                        </span>
                      </div>
                    </td>
                    <td className={styles.tdAction}>
                      <div className={styles.actionContainer}>
                        <Tooltip content="View" bgColor="bg-green-600">
                          <Link
                            to={`/product-view/${product.id}`}
                            className="inline-flex"
                          >
                            <button className={styles.viewButton}>
                              <FontAwesomeIcon
                                icon={faEye}
                                className="text-white text-sm"
                              />
                            </button>
                          </Link>
                        </Tooltip>
                        <Tooltip content="Edit" bgColor="bg-[#2f6aa7]">
                          <Link
                            to={`/product-edit/${product.id}`}
                            className="inline-flex"
                          >
                            <button className={styles.editButton}>
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="text-white text-sm"
                              />
                            </button>
                          </Link>
                        </Tooltip>
                        <Tooltip content="Delete" bgColor="bg-red-600">
                          <button
                            className={styles.deleteButton}
                            onClick={() => handleDeleteClick(product.id)}
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="text-white text-sm"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                    <td className={styles.tdAction}>
                      <LogButton log={displayValue(product.log)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr className={styles.loadingRow}>
                  <td colSpan={9} className={styles.noDataText}>
                    No records match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          filteredLength={filteredProducts.length}
          handlePageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} className={styles.modal}>
        <button
          onClick={closeModal}
          className={styles.modalCloseButton}
        ></button>
        <div className="flex justify-center">
          <div className={styles.modalIconContainer}>
            <FontAwesomeIcon icon={faTrash} className={styles.modalIcon} />
          </div>
        </div>
        <h4 className={styles.modalTitle}>Confirm Delete</h4>
        <p className={styles.modalText}>
          Are you sure you want to delete this product?
        </p>
        <div className={styles.modalButtonContainer}>
          <button onClick={closeModal} className={styles.modalCancelButton}>
            Cancel
          </button>
          <button
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isSuccessModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
            <CheckCircle className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          {successMessage}
        </h4>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => setSuccessModalOpen(false)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            OK
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={isErrorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        className="w-[80%] max-w-[400px] bg-red-100 dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
      >
        <div className="flex justify-center">
          <div className="w-14 h-14 flex items-center justify-center bg-yellow-200 text-yellow-600 rounded-full">
            <TriangleAlert className="w-6 h-6" />
          </div>
        </div>
        <h4 className="text-theme-xl font-bold text-gray-800 dark:text-white mt-4">
          Alert!
        </h4>
        <p className="text-gray-600 dark:text-gray-300 mt-2">{errorMessage}</p>
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => {
              setErrorModalOpen(false);
              if (errorMessage === "Can't delete this product because it's assigned to subcategory.") {
                window.location.href = "/product-list";
              }
            }}
            className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600"
          >
            OK
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ProductTable;